/**
 * State machine for admin master-data list pages.
 *
 * Handles list fetch (debounced search / pagination / sort), the
 * create/edit modal, the delete modal, and toasts on success/error.
 * The caller provides a `service` (list/create/update/remove) and
 * form component; this hook wires everything up.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  PAGE_SIZE_OPTIONS,
} from "../components/ui/Pagination";

function extractErrorMessage(err, tGeneric, tDuplicate, tInUseBody, name, tInUseTitle) {
  const detail = err?.response?.data?.detail;
  if (detail === "DUPLICATE") {
    return { message: tDuplicate };
  }
  if (detail && typeof detail === "object" && detail.code === "IN_USE") {
    const listing = (detail.products || []).map((p) => p.name).join(", ");
    return {
      message: tInUseBody({ name, items: listing || "—" }),
      title: tInUseTitle,
    };
  }
  return { message: tGeneric };
}

export function useCrudList({ service, defaultSort, defaultPageSize = 10, sessionKey }) {
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(() => {
    if (!sessionKey) return defaultPageSize;
    try {
      const raw = sessionStorage.getItem(sessionKey);
      const n = raw ? Number(raw) : NaN;
      return PAGE_SIZE_OPTIONS.includes(n) ? n : defaultPageSize;
    } catch { return defaultPageSize; }
  });
  const setPageSize = useCallback((n) => {
    const size = PAGE_SIZE_OPTIONS.includes(n) ? n : defaultPageSize;
    setPageSizeState(size);
    setPage(1);
    if (sessionKey) {
      try { sessionStorage.setItem(sessionKey, String(size)); } catch { /* ignore */ }
    }
  }, [sessionKey, defaultPageSize]);
  const [sortKey, setSortKey] = useState(defaultSort.key);
  const [sortDir, setSortDir] = useState(defaultSort.dir);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const d = await service.list({
        search: search || undefined,
        sort: `${sortKey}:${sortDir}`,
        page,
        page_size: pageSize,
      });
      setItems(d.items || []);
      setTotal(d.total || 0);
    } catch {
      toast.error(t("admin.common.generic_error"));
    } finally {
      setLoading(false);
    }
  }, [service, search, sortKey, sortDir, page, pageSize, t]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openCreate = () => {
    setEditingRow(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRow(null);
  };

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editingRow) {
        await service.update(editingRow.id, payload);
        toast.success(t("admin.common.saved"));
      } else {
        await service.create(payload);
        toast.success(t("admin.common.created"));
      }
      closeModal();
      await reload();
      return true;
    } catch (err) {
      const info = extractErrorMessage(
        err,
        t("admin.common.generic_error"),
        t("admin.common.duplicate"),
        (opts) => t("admin.common.in_use_body", opts),
        payload?.name || editingRow?.name || "",
        t("admin.common.in_use_title")
      );
      toast.error(info.message, info.title ? { description: info.title } : undefined);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (row) => setDeleteRow(row);
  const cancelDelete = () => setDeleteRow(null);

  const confirmDelete = async () => {
    if (!deleteRow) return;
    setDeleting(true);
    try {
      await service.remove(deleteRow.id);
      toast.success(t("admin.common.deleted"));
      cancelDelete();
      await reload();
    } catch (err) {
      const info = extractErrorMessage(
        err,
        t("admin.common.generic_error"),
        t("admin.common.duplicate"),
        (opts) => t("admin.common.in_use_body", opts),
        deleteRow?.name || deleteRow?.postcode || "",
        t("admin.common.in_use_title")
      );
      toast.error(info.message, info.title ? { description: info.title } : undefined);
      cancelDelete();
    } finally {
      setDeleting(false);
    }
  };

  const changeSort = (key, dir) => {
    setSortKey(key);
    setSortDir(dir);
    setPage(1);
  };

  return useMemo(
    () => ({
      items,
      total,
      page,
      pageSize,
      loading,
      sortKey,
      sortDir,
      searchValue: searchInput,
      setSearchValue: setSearchInput,
      setPage,
      setPageSize,
      changeSort,
      modalOpen,
      editingRow,
      openCreate,
      openEdit,
      closeModal,
      save,
      saving,
      deleteRow,
      askDelete,
      cancelDelete,
      confirmDelete,
      deleting,
      reload,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      items,
      total,
      page,
      pageSize,
      loading,
      sortKey,
      sortDir,
      searchInput,
      modalOpen,
      editingRow,
      saving,
      deleteRow,
      deleting,
    ]
  );
}
